import { Button, Collapse, Divider, Drawer, List, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import ExpandMore from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/MenuOpen";
import clsx from "clsx";
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { _sidebarColorLight, _sidebarColorDark, secondaryColor, secondaryColorWithOpacity } from "../../../theme/Theme";

const useStyles = makeStyles({
    list: {
        width: 250,
    },
    fullList: {
        width: "auto",
    },
});

const listItemStyle = (isOpen) => ({
    color: isOpen ? secondaryColor : "inherit",
    "&:hover": {
        color: secondaryColor,
    },
});

const chosenListItemStyle = (url, currentUrl) => ({
    backgroundColor: url === currentUrl ? secondaryColorWithOpacity : null,
    borderTopRightRadius: 50,
    borderBottomRightRadius: 50,
    "&:hover": {
        backgroundColor: url === currentUrl ? secondaryColorWithOpacity : null,
    },
});

const expandIconStyle = (isOpen) => ({
    transform: isOpen ? "rotate(-180deg)" : "",
    transition: "transform 500ms ease",
});

export default function Sidebar(props) {
    const theme = useTheme();
    const classes = useStyles();
    const [state, setState] = useState({
        top: false,
        left: false,
        bottom: false,
        right: false,
    });

    const toggleDrawer = (anchor, open) => (event) => {
        if (event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) {
            return;
        }
        setState({ ...state, [anchor]: open });
    };

    const [openSidebarItems, setOpenSidebarItems] = useState([false, false, false, false, false, false, false, false]);
    const { t } = useTranslation("translation");

    const toggleSidebarItems = (index) => {
        const updatedOpenSidebarItems = Array(8).fill(false);
        updatedOpenSidebarItems[index] = !openSidebarItems[index];
        setOpenSidebarItems(updatedOpenSidebarItems);
    };

    const location = useLocation();

    const list = (anchor) => (
        <div
            id="sb_PageList"
            className={clsx(classes.list, {
                [classes.fullList]: anchor === "top" || anchor === "bottom",
            })}
            role="presentation"
            onClick={toggleDrawer(anchor, false)}
            onKeyDown={toggleDrawer(anchor, false)}
        >
            <List sx={{ backgroundColor: theme.palette.mode === "dark" ? _sidebarColorDark : _sidebarColorLight }}>
                <ListItem id="sb_ListItem_1" button onClick={props.onClose} style={{ marginBottom: -3, marginTop: -3 }}>
                    <ListItemIcon style={{ marginBottom: 5, marginLeft: 5, color: "#fff" }}>
                        {" "}
                        <MenuIcon />{" "}
                    </ListItemIcon>
                    <Typography className={classes.title} component={Link} to="/" noWrap style={{ cursor: "pointer", textDecoration: "none", marginTop: 2 }}>
                        <Button id="sb_Button_1" >
                            <img src={"most_dark.png"} alt="MOST" width="90" />
                        </Button>
                    </Typography>
                </ListItem>
            </List>
            <Divider />

            {/* Area Definitons */}
            <ListItem id="sb_ListItem_2" button onClick={() => toggleSidebarItems(0)} sx={listItemStyle(openSidebarItems[0])}>
                <ListItemText primary={t("AreaDefinitions")} />
                <ExpandMore sx={expandIconStyle(openSidebarItems[0])} />
            </ListItem>
            <Collapse in={openSidebarItems[0]} timeout="auto" unmountOnExit>
                <List>
                    {props.sidebarAreaArray.map((item, index) => (
                        <ListItem
                            id={`sb_ListItem_2_Sub_${index}`}
                            button
                            key={item.link}
                            component={Link}
                            to={item.link}
                            onClick={props.onClose}
                            sx={chosenListItemStyle(item.link, location.pathname)}
                        >
                            <ListItemIcon> {item.icon} </ListItemIcon>
                            <ListItemText primary={t(item.name)} />
                        </ListItem>
                    ))}
                </List>
            </Collapse>

            {/* Breakdown Definitons */}
            <ListItem id="sb_ListItem_3" button onClick={() => toggleSidebarItems(1)} sx={listItemStyle(openSidebarItems[1])}>
                <ListItemText primary={t("BreakdownDefinitions")} />
                <ExpandMore sx={expandIconStyle(openSidebarItems[1])} />
            </ListItem>
            <Collapse in={openSidebarItems[1]} timeout="auto" unmountOnExit>
                <List>
                    {props.sidebarBreakdownArray.map((item, index) => (
                        <ListItem
                            id={`sb_ListItem_3_Sub_${index}`}
                            button
                            key={item.link}
                            component={Link}
                            to={item.link}
                            onClick={props.onClose}
                            sx={chosenListItemStyle(item.link, location.pathname)}
                        >
                            <ListItemIcon> {item.icon} </ListItemIcon>
                            <ListItemText primary={t(item.name)} />
                        </ListItem>
                    ))}
                </List>
            </Collapse>

            {/* Equipment Definitons */}
            <ListItem id="sb_ListItem_4" button onClick={() => toggleSidebarItems(2)} sx={listItemStyle(openSidebarItems[2])}>
                <ListItemText primary={t("EquipmentDefinitions")} />
                <ExpandMore sx={expandIconStyle(openSidebarItems[2])} />
            </ListItem>
            <Collapse in={openSidebarItems[2]} timeout="auto" unmountOnExit>
                <List>
                    {props.sidebarEquipmentArray.map((item, index) => (
                        <ListItem
                            id={`sb_ListItem_4_Sub_${index}`}
                            button
                            key={item.link}
                            component={Link}
                            to={item.link}
                            onClick={props.onClose}
                            sx={chosenListItemStyle(item.link, location.pathname)}
                        >
                            <ListItemIcon> {item.icon} </ListItemIcon>
                            <ListItemText primary={t(item.name)} />
                        </ListItem>
                    ))}
                </List>
            </Collapse>

            {/* User Definitons */}
            <ListItem id="sb_ListItem_5" button onClick={() => toggleSidebarItems(3)} sx={listItemStyle(openSidebarItems[3])}>
                <ListItemText primary={t("UserDefinitions")} />
                <ExpandMore sx={expandIconStyle(openSidebarItems[3])} />
            </ListItem>
            <Collapse in={openSidebarItems[3]} timeout="auto" unmountOnExit>
                <List>
                    {props.sidebarUserArray.map((item, index) => (
                        <ListItem
                            id={`sb_ListItem_5_Sub_${index}`}
                            button
                            key={item.link}
                            component={Link}
                            to={item.link}
                            onClick={props.onClose}
                            sx={chosenListItemStyle(item.link, location.pathname)}
                        >
                            <ListItemIcon> {item.icon} </ListItemIcon>
                            <ListItemText primary={t(item.name)} />
                        </ListItem>
                    ))}
                </List>
            </Collapse>

            {/* Planned Maintenance */}
            <ListItem id="sb_ListItem_6" button onClick={() => toggleSidebarItems(4)} sx={listItemStyle(openSidebarItems[4])}>
                <ListItemText primary={t("MaintenanceLists")} />
                <ExpandMore sx={expandIconStyle(openSidebarItems[4])} />
            </ListItem>
            <Collapse in={openSidebarItems[4]} timeout="auto" unmountOnExit>
                <List>
                    {props.sideBarPlannedMaintenanceArray.map((item, index) => (
                        <ListItem
                            id={`sb_ListItem_6_Sub_${index}`}
                            button
                            key={item.link}
                            component={Link}
                            to={item.link}
                            onClick={props.onClose}
                            sx={chosenListItemStyle(item.link, location.pathname)}
                        >
                            <ListItemIcon> {item.icon} </ListItemIcon>
                            <ListItemText primary={t(item.name)} />
                        </ListItem>
                    ))}
                </List>
            </Collapse>

            {/* Autonomous Maintenance */}
            <ListItem id="sb_ListItem_7" button onClick={() => toggleSidebarItems(5)} sx={listItemStyle(openSidebarItems[5])}>
                <ListItemText primary={t("AutonomousMaintenanceLists")} />
                <ExpandMore sx={expandIconStyle(openSidebarItems[5])} />
            </ListItem>
            <Collapse in={openSidebarItems[5]} timeout="auto" unmountOnExit>
                <List>
                    {props.sideBarAutonomousMaintenanceArray.map((item, index) => (
                        <ListItem
                            id={`sb_ListItem_7_Sub_${index}`}
                            button
                            key={item.link}
                            component={Link}
                            to={item.link}
                            onClick={props.onClose}
                            sx={chosenListItemStyle(item.link, location.pathname)}
                        >
                            <ListItemIcon> {item.icon} </ListItemIcon>
                            <ListItemText primary={t(item.name)} />
                        </ListItem>
                    ))}
                </List>
            </Collapse>

            {/* Excel Upload */}
            <ListItem id="sb_ListItem_8" button onClick={() => toggleSidebarItems(6)} sx={listItemStyle(openSidebarItems[6])}>
                <ListItemText primary={t("DataUpload")} />
                <ExpandMore sx={expandIconStyle(openSidebarItems[6])} />
            </ListItem>
            <Collapse in={openSidebarItems[6]} timeout="auto" unmountOnExit>
                <List>
                    {props.sideBarExcelUploadArray.map((item, index) => (
                        <ListItem
                            id={`sb_ListItem_8_Sub_${index}`}
                            button
                            key={item.link}
                            component={Link}
                            to={item.link}
                            onClick={props.onClose}
                            sx={chosenListItemStyle(item.link, location.pathname)}
                        >
                            <ListItemIcon> {item.icon} </ListItemIcon>
                            <ListItemText primary={t(item.name)} />
                        </ListItem>
                    ))}
                </List>
            </Collapse>

            {/* Mail Groups */}
            <ListItem id="sb_ListItem_9" button onClick={() => toggleSidebarItems(7)} sx={listItemStyle(openSidebarItems[7])}>
                <ListItemText primary={t("MailGroups")} />
                <ExpandMore sx={expandIconStyle(openSidebarItems[7])} />
            </ListItem>
            <Collapse in={openSidebarItems[7]} timeout="auto" unmountOnExit>
                <List>
                    {props.sideBarMailGroupsArray.map((item, index) => (
                        <ListItem
                            id={`sb_ListItem_9_Sub_${index}`}
                            button
                            key={item.link}
                            component={Link}
                            to={item.link}
                            onClick={props.onClose}
                            sx={chosenListItemStyle(item.link, location.pathname)}
                        >
                            <ListItemIcon> {item.icon} </ListItemIcon>
                            <ListItemText primary={t(item.name)} />
                        </ListItem>
                    ))}
                </List>
            </Collapse>
        </div>
    );

    return (
        <div>
            <React.Fragment key={props.anchor}>
                {props.userRight.includes("MainDataMaintenance") || props.userRight.includes("VIEWER") ? (
                    <Drawer anchor={props.anchor} open={props.shouldOpen} onClose={props.onClose}>
                        {list(props.anchor)}
                    </Drawer>
                ) : null}
            </React.Fragment>
        </div>
    );
}
